/**
 * HEDEF: GOOGLE DRIVE — uygulamaya ayrılmış gizli klasör (`appDataFolder`).
 *
 * Kapsam `drive.appdata`: Parafon yalnızca KENDİ gizli klasörünü görür;
 * kullanıcının Drive'daki dosyalarına erişemez. Klasör Drive arayüzünde
 * görünmez (Ayarlar › Uygulamaları yönet'ten silinebilir); geri yükleme
 * listesini Parafon kendisi çıkarır.
 *
 * SINIR — SAAT BAŞI TEK TIK: Google, sırrı olmayan (yalnızca tarayıcıda
 * koşan) uygulamalara YENİLEME ANAHTARI vermiyor; kod değişimi istemci sırrı
 * istiyor ve sırrı tutacak bir sunucu yok (kullanıcı kararı: sunucu hiçbir
 * şey tutmaz). Bu yüzden belirteç akışı kullanılıyor: erişim anahtarı ~1
 * saat. Süre dolunca yedek BEKLER ve "Google'a yeniden bağlan" düğmesi
 * görünür; Google önceden verilen izni hatırladığı için tık çoğunlukla
 * pencereyi kendiliğinden kapatır. Bu sınırı istemeyen kullanıcı için
 * "Eşitlenen klasör" + Google Drive masaüstü istemcisi aynı buluta sürekli
 * yazar.
 */

import { M } from "../../dil";
import type { BulutDosyasi, BulutHedefi, HedefKaydi } from "../tipler";
import { BaglantiGerekliHatasi } from "../yedekleyici";
import { bitisHesapla, donusAdresi, gecerliMi, rastgele } from "../oauth";
import type { BulutBagimliliklari } from "../oauth";

const YETKI = "https://accounts.google.com/o/oauth2/v2/auth";
const API = "https://www.googleapis.com/drive/v3";
const YUKLE = "https://www.googleapis.com/upload/drive/v3";
const KAPSAM = "https://www.googleapis.com/auth/drive.appdata";

type GoogleKaydi = Extract<HedefKaydi, { tur: "google-drive" }>;

export async function googleBaglan(istemci: string, d: BulutBagimliliklari): Promise<GoogleKaydi> {
  const state = rastgele();
  const params = await d.yetkilendir(
    () =>
      Promise.resolve(
        `${YETKI}?${new URLSearchParams({
          client_id: istemci,
          redirect_uri: donusAdresi(),
          response_type: "token",
          scope: KAPSAM,
          include_granted_scopes: "true",
          state,
        }).toString()}`,
      ),
    state,
  );
  const erisim = params.get("access_token");
  if (erisim === null) throw new Error(M.yedek.hedef.googleAnahtarYok);
  if (!(params.get("scope") ?? KAPSAM).split(" ").includes(KAPSAM)) {
    throw new Error(M.yedek.hedef.googleIzinYok);
  }
  return {
    tur: "google-drive",
    erisim: { deger: erisim, bitis: bitisHesapla(d.saat(), Number(params.get("expires_in"))) },
  };
}

interface DriveDosyasi {
  readonly id: string;
  readonly name: string;
  readonly modifiedTime?: string;
  readonly size?: string;
}

export function googleDriveHedefi(ilk: GoogleKaydi, istemci: string, d: BulutBagimliliklari): BulutHedefi {
  let kayit: GoogleKaydi = ilk;

  function anahtar(): string {
    if (kayit.erisim === null || !gecerliMi(kayit.erisim.bitis, d.saat())) {
      throw new BaglantiGerekliHatasi(M.yedek.hedef.googleSureDoldu);
    }
    return kayit.erisim.deger;
  }

  async function cagir(url: string, init: RequestInit = {}): Promise<Response> {
    const y = await d.fetch(url, {
      ...init,
      headers: { ...(init.headers as Record<string, string> | undefined), Authorization: `Bearer ${anahtar()}` },
    });
    if (y.status === 401) {
      kayit = { ...kayit, erisim: null };
      throw new BaglantiGerekliHatasi(M.yedek.hedef.googleReddetti);
    }
    if (!y.ok) {
      const metin = await y.text().catch(() => "");
      throw new Error(`Google Drive ${y.status}: ${metin.slice(0, 200)}`);
    }
    return y;
  }

  async function hepsi(): Promise<DriveDosyasi[]> {
    const out: DriveDosyasi[] = [];
    let sayfa: string | null = null;
    do {
      const q = new URLSearchParams({
        spaces: "appDataFolder",
        fields: "nextPageToken,files(id,name,modifiedTime,size)",
        pageSize: "1000",
      });
      if (sayfa !== null) q.set("pageToken", sayfa);
      const g = (await (await cagir(`${API}/files?${q.toString()}`)).json()) as {
        files?: DriveDosyasi[];
        nextPageToken?: string;
      };
      out.push(...(g.files ?? []));
      sayfa = g.nextPageToken ?? null;
    } while (sayfa !== null);
    return out;
  }

  async function bul(ad: string): Promise<DriveDosyasi | null> {
    return (await hepsi()).find((f) => f.name === ad) ?? null;
  }

  return {
    tur: "google-drive",
    konum: M.yedek.hedef.googleKonum,
    hazirlik: () =>
      Promise.resolve(
        kayit.erisim !== null && gecerliMi(kayit.erisim.bitis, d.saat()) ? "hazir" : "etkilesim-gerekli",
      ),
    async yenidenBaglan() {
      kayit = await googleBaglan(istemci, d);
    },
    async yaz(ad, metin) {
      const var_ = await bul(ad);
      if (var_ !== null) {
        await cagir(`${YUKLE}/files/${encodeURIComponent(var_.id)}?uploadType=media`, {
          method: "PATCH",
          headers: { "Content-Type": "application/octet-stream" },
          body: metin,
        });
        return;
      }
      const sinir = `parafon-${rastgele(12)}`;
      const govde =
        `--${sinir}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n` +
        `${JSON.stringify({ name: ad, parents: ["appDataFolder"] })}\r\n` +
        `--${sinir}\r\nContent-Type: application/octet-stream\r\n\r\n${metin}\r\n--${sinir}--`;
      await cagir(`${YUKLE}/files?uploadType=multipart`, {
        method: "POST",
        headers: { "Content-Type": `multipart/related; boundary=${sinir}` },
        body: govde,
      });
    },
    async listele() {
      return (await hepsi())
        .filter((f) => f.name.endsWith(".pfai"))
        .map(
          (f): BulutDosyasi => ({
            ad: f.name,
            degisti: f.modifiedTime ?? null,
            boyut: f.size === undefined ? null : Number(f.size),
          }),
        );
    },
    async oku(ad) {
      const f = await bul(ad);
      if (f === null) throw new Error(M.yedek.hedef.googleDosyaYok(ad));
      return (await cagir(`${API}/files/${encodeURIComponent(f.id)}?alt=media`)).text();
    },
    async sil(ad) {
      const f = await bul(ad);
      if (f === null) return;
      await cagir(`${API}/files/${encodeURIComponent(f.id)}`, { method: "DELETE" });
    },
    kayit: () => kayit,
  };
}
